import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { useApolloClient, useQuery } from '@apollo/client';
import { useStore } from 'zustand';

import {
  DrawerContext,
  ModalContext,
  ModalModel,
  Text,
} from '@notino/react-styleguide';
import {
  GetReviewsQuery,
  GetReviewsQueryVariables,
  ReviewOrderBy,
} from '@notino/shared/definitions/types';

import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import getReviewsQuery from '@queries/useReviews/queries/reviews.graphql';
import { isDesktop } from '@utils/helpers';

import { DrawerHeaderTitle } from './DrawerHeaderTitle';
import { PhotosGrid } from './PhotosGrid';
import {
  createPhotosStore,
  mapPhotos,
  PhotoWithReview,
} from './store/createPhotosStore';
import {
  customHeaderClass,
  PhotoWrapper,
  PhotosWrapper,
  StyledOverflowFade,
  Wrapper,
  PhotoOverlay,
} from './styled';

const MAX_PHOTOS_TO_SHOW = 8;

export const AllReviewPhotos = () => {
  const { product } = useProductDetailContext();
  const { data } = useQuery<GetReviewsQuery, GetReviewsQueryVariables>(
    getReviewsQuery,
    {
      variables: {
        masterProductId: product.catalogMasterId,
        orderBy: ReviewOrderBy.DateTime,
        orderDesc: true,
        page: 1,
        contentTypes: ['WithImages'],
      },
      ssr: true,
    }
  );

  const { reviews, totalPages } = data?.reviewsAndFilters?.data ?? {
    totalPages: 0,
  };

  if (!reviews || reviews.flatMap((x) => x.images).length < 2) {
    return null;
  }

  return (
    <AllReviewPhotosWithInitialData reviews={data!} totalPages={totalPages} />
  );
};

const AllReviewPhotosWithInitialData = ({
  reviews,
  totalPages,
}: {
  reviews: GetReviewsQuery;
  totalPages: number;
}) => {
  const apollo = useApolloClient();
  const { product } = useProductDetailContext();
  const [store] = React.useState(() =>
    createPhotosStore({
      apollo,
      hasMore: totalPages > 1,
      initialPhotos: mapPhotos(reviews),
      masterProductId: product.catalogMasterId,
    })
  );
  const { photos, hasMore } = useStore(store);
  const { openDrawer } = DrawerContext.useDrawerContext();
  const { toggleModal } = ModalContext.useModalContext();

  const handleShowAllPhotos = (image?: PhotoWithReview) => {
    store.setState({ selectedPhoto: image });
    if (isDesktop()) {
      toggleModal(<PhotosGrid store={store} />, {
        noBorders: true,
        size: ModalModel.ModalSizes.s1100,
        withFocusTrap: true,
      });
    } else {
      openDrawer(<PhotosGrid store={store} />, {
        initialPosition: 'top',
        isTitleBorderHidden: true,
        hideScrollbar: true,
        headerCustomStyles: customHeaderClass,
        title: <DrawerHeaderTitle store={store} />,
      });
    }
  };

  const photosToShow = photos.slice(0, MAX_PHOTOS_TO_SHOW);
  const hasMorePhotosToShow = photos.length > photosToShow.length || hasMore;

  return (
    <Wrapper>
      <Text typography="labelRegular" marginBottom="12px">
        <FormattedMessage
          id="reviews.allReviewPhotos.sectionTitle"
          defaultMessage="Fotky od vás"
        />
      </Text>

      <StyledOverflowFade
        config={{ scrollAmount: 600 }}
        arrowOffset="1rem"
        arrowType="chevron-in-circle"
        showFadeOnlyOn="desktop"
      >
        <PhotosWrapper>
          {photosToShow.map((image, i) => {
            const isLastAndHasMore =
              hasMorePhotosToShow && i === photosToShow.length - 1;
            const photoAlt = `Review photo ${i + 1}`;

            return (
              <PhotoWrapper
                type="button"
                key={image.fullSizeUrl}
                onClick={() =>
                  handleShowAllPhotos(isLastAndHasMore ? undefined : image)
                }
              >
                <img src={image.thumbnailUrl ?? undefined} alt={photoAlt} />

                {isLastAndHasMore && (
                  <PhotoOverlay>
                    <FormattedMessage
                      id="reviews.allReviewPhotos.showAllButton"
                      defaultMessage="Zobrazit vše"
                    />
                  </PhotoOverlay>
                )}
              </PhotoWrapper>
            );
          })}
        </PhotosWrapper>
      </StyledOverflowFade>
    </Wrapper>
  );
};
